// Client for the Kizuna relay (infra/relay in Warnster/kizuna): HTTP room API plus the
// binary WebSocket protocol, including the LINK stratum (docs/68 section 3).

export const RELAY_ORIGIN = "https://relay.kizuna.trading";

export type Family = "gba-cable" | "gba-rfu";

export const Frame = {
  Welcome: 0x00,
  Ping: 0x01,
  Pong: 0x02,
  PeerUp: 0x03,
  PeerDown: 0x04,
  SessionClose: 0x05,
  LinkStatus: 0x40,
  LinkCmd: 0x41,
  LinkData: 0x42,
  LinkRequest: 0x43,
  LinkAck: 0x44,
} as const;

const HEADER = 4;
const LINK_DATA_FRAME = HEADER + 4 + 64; // 72

export interface Seat {
  code: string;
  seat: "A" | "B";
  token: string;
}

export class RelayHttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function post(path: string, body: unknown): Promise<any> {
  // No content-type header: keeps it a "simple" request (no CORS preflight); the relay parses JSON anyway.
  const res = await fetch(RELAY_ORIGIN + path, { method: "POST", body: JSON.stringify(body) });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new RelayHttpError(json.error ?? `relay error ${res.status}`, res.status);
  return json;
}

export async function createRoom(family: Family): Promise<Seat> {
  const r = await post("/v1/room", { family });
  return { code: r.code, seat: r.seat, token: r.token };
}

export async function joinRoom(code: string, family: Family): Promise<Seat> {
  const r = await post(`/v1/room/${encodeURIComponent(code)}/join`, { family });
  return { code: r.code, seat: r.seat, token: r.token };
}

export const ROOM_CODE_RE = /^KZN-[A-HJ-KM-NP-Z2-9]{4}$/;

export function normaliseRoomCode(input: string): string {
  let s = input.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (s.startsWith("KZN")) s = s.slice(3);
  return `KZN-${s}`;
}

export interface RelayEvents {
  welcome(seat: "A" | "B", peerPresent: boolean): void;
  peerUp(): void;
  peerDown(dropped: boolean): void;
  sessionClose(): void;
  linkCmd(cmd: number): void;
  /** In-order, de-duplicated peer frames, ready to write to the device. */
  linkData(frame: Uint8Array<ArrayBuffer>): void;
  socketState(state: "connecting" | "open" | "reconnecting" | "closed"): void;
  log(text: string): void;
}

// Delivery timings follow the ESP client (kzn_link_session.h).
const GAP_REQUEST_AFTER_MS = 40;
const GAP_REPEAT_MS = 250;
const GAP_GIVE_UP_MS = 1500;
const ACK_EVERY_MS = 50;
const ACK_EVERY_FRAMES = 8;
const PING_EVERY_MS = 20_000;
const RECONNECT_MS = 2000;
const REORDER_MAX = 64;

export class RelaySocket {
  private ws: WebSocket | null = null;
  private seq = 0;
  private upDseq = 0;
  private closedByUs = false;
  private lastStatus: number | null = null;

  // Receive side.
  private expected = 0;
  private held = new Map<number, Uint8Array<ArrayBuffer>>();
  private gapSince = 0;
  private gapLastRequest = 0;
  private lastAcked = -1;
  private deliveredSinceAck = 0;
  private lastAckAt = 0;

  private tick: ReturnType<typeof setInterval> | null = null;
  private ping: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly token: string,
    private readonly on: RelayEvents,
  ) {}

  connect(): void {
    this.closedByUs = false;
    this.open("connecting");
    this.tick = setInterval(() => this.onTick(), 25);
    this.ping = setInterval(() => this.send(Frame.Ping, u32(Date.now() >>> 0)), PING_EVERY_MS);
  }

  close(): void {
    this.closedByUs = true;
    if (this.tick) clearInterval(this.tick);
    if (this.ping) clearInterval(this.ping);
    this.ws?.close(1000, "bye");
    this.ws = null;
    this.on.socketState("closed");
  }

  /** Forward a device status (0xFF01..0xFF07) to the relay. */
  sendStatus(code: number): void {
    this.lastStatus = code;
    const p = new Uint8Array(2);
    new DataView(p.buffer).setUint16(0, code, true);
    this.send(Frame.LinkStatus, p);
  }

  /** Forward a 64-byte device frame to the peer. */
  sendLinkData(frame: Uint8Array): void {
    const p = new Uint8Array(68);
    new DataView(p.buffer).setUint32(0, this.upDseq++ >>> 0, true);
    p.set(frame, 4);
    this.send(Frame.LinkData, p);
  }

  private open(state: "connecting" | "reconnecting"): void {
    this.on.socketState(state);
    const ws = new WebSocket(
      `${RELAY_ORIGIN.replace(/^http/, "ws")}/v1/relay?token=${encodeURIComponent(this.token)}`,
    );
    ws.binaryType = "arraybuffer";
    ws.onopen = () => this.on.socketState("open");
    ws.onmessage = (e) => {
      if (e.data instanceof ArrayBuffer) this.onMessage(new Uint8Array(e.data));
    };
    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.closedByUs || e.code === 4001) return;
      if (e.code === 1000 && e.reason === "session closed") return;
      this.on.log(`relay connection lost (${e.code}); reconnecting`);
      setTimeout(() => !this.closedByUs && this.open("reconnecting"), RECONNECT_MS);
    };
    this.ws = ws;
  }

  private send(type: number, payload: Uint8Array = new Uint8Array(0)): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const f = new Uint8Array(HEADER + payload.length);
    f[0] = 1;
    f[1] = type;
    new DataView(f.buffer).setUint16(2, this.seq++ & 0xffff, true);
    f.set(payload, HEADER);
    this.ws.send(f);
  }

  private onMessage(buf: Uint8Array): void {
    // A batch is k whole LINK_DATA frames back to back.
    if (buf.length > LINK_DATA_FRAME && buf.length % LINK_DATA_FRAME === 0 && buf[1] === Frame.LinkData) {
      for (let i = 0; i < buf.length; i += LINK_DATA_FRAME) this.onFrame(buf.subarray(i, i + LINK_DATA_FRAME));
      return;
    }
    this.onFrame(buf);
  }

  private onFrame(f: Uint8Array): void {
    if (f.length < HEADER) return;
    const type = f[1];
    const p = f.subarray(HEADER);
    switch (type) {
      case Frame.Welcome: {
        const seat = p[0] === 1 ? "B" : "A";
        this.on.welcome(seat, p[1] === 1);
        // The relay does not remember who was master, so never re-send AwaitMode on reconnect.
        if (this.lastStatus !== null && this.lastStatus !== 0xff02) this.sendStatus(this.lastStatus);
        break;
      }
      case Frame.PeerUp:
        this.on.peerUp();
        break;
      case Frame.PeerDown:
        this.on.peerDown(p[0] === 1);
        break;
      case Frame.SessionClose:
        this.on.sessionClose();
        break;
      case Frame.LinkCmd:
        if (p.length >= 1) this.on.linkCmd(p[0]);
        break;
      case Frame.LinkData:
        if (p.length === 68) {
          const dseq = new DataView(p.buffer, p.byteOffset, 4).getUint32(0, true);
          this.receive(dseq, p.slice(4));
        }
        break;
    }
  }

  private receive(dseq: number, data: Uint8Array<ArrayBuffer>): void {
    if (dseq < this.expected || this.held.has(dseq)) return; // duplicate
    if (dseq === this.expected) {
      this.deliver(data);
      this.drain();
    } else if (this.held.size < REORDER_MAX) {
      this.held.set(dseq, data);
      if (!this.gapSince) this.gapSince = performance.now();
    }
  }

  private deliver(data: Uint8Array<ArrayBuffer>): void {
    this.on.linkData(data);
    this.expected++;
    this.deliveredSinceAck++;
  }

  private drain(): void {
    for (let d = this.held.get(this.expected); d; d = this.held.get(this.expected)) {
      this.held.delete(this.expected);
      this.deliver(d);
    }
    this.gapSince = this.held.size ? performance.now() : 0;
    this.gapLastRequest = 0;
  }

  private onTick(): void {
    const now = performance.now();
    if (this.held.size && this.gapSince) {
      if (now - this.gapSince > GAP_GIVE_UP_MS) {
        const lowest = Math.min(...this.held.keys());
        this.on.log(`gave up on frames ${this.expected}..${lowest - 1}`);
        this.expected = lowest;
        this.drain();
      } else if (
        now - this.gapSince > GAP_REQUEST_AFTER_MS &&
        (!this.gapLastRequest || now - this.gapLastRequest > GAP_REPEAT_MS)
      ) {
        const lowest = Math.min(...this.held.keys());
        const missing: number[] = [];
        for (let d = this.expected; d < lowest && missing.length < 16; d++) missing.push(d);
        const p = new Uint8Array(missing.length * 4);
        const v = new DataView(p.buffer);
        missing.forEach((d, i) => v.setUint32(i * 4, d >>> 0, true));
        this.send(Frame.LinkRequest, p);
        this.gapLastRequest = now;
      }
    }
    const top = this.expected - 1;
    if (
      top > this.lastAcked &&
      (this.deliveredSinceAck >= ACK_EVERY_FRAMES || now - this.lastAckAt > ACK_EVERY_MS)
    ) {
      this.send(Frame.LinkAck, u32(top));
      this.lastAcked = top;
      this.deliveredSinceAck = 0;
      this.lastAckAt = now;
    }
  }
}

function u32(n: number): Uint8Array {
  const p = new Uint8Array(4);
  new DataView(p.buffer).setUint32(0, n >>> 0, true);
  return p;
}
